import {
  normalizeError,
  type ObservabilityContext,
} from '@brew/observability/core'
import {
  contextSpanAttributes,
  recordSpanError,
  sanitizeSpanAttributes,
} from '@brew/observability/instrumentation'
import {
  classifyMetricError,
  defineMetric,
  type MetricTags,
  metricNow,
  recordMetric,
  registerMetricErrorOutcome,
} from '@brew/observability/metrics'
import { context, type Span, SpanStatusCode, trace } from '@opentelemetry/api'

const tracer = trace.getTracer('brew.external-calls')
const tags = [
  'provider',
  'operation',
  'kind',
  'outcome',
  'status_class',
  'domain',
  'transport',
  'method',
] as const

export const dependencyMetrics = {
  completed: defineMetric({
    name: 'brew.dependency.completed',
    type: 'count',
    unit: 'count',
    tags,
  }),
  duration: defineMetric({
    name: 'brew.dependency.duration',
    type: 'distribution',
    unit: 'millisecond',
    tags,
  }),
  attempts: defineMetric({
    name: 'brew.dependency.attempts',
    type: 'count',
    unit: 'count',
    tags,
  }),
  attemptDuration: defineMetric({
    name: 'brew.dependency.attempt.duration',
    type: 'distribution',
    unit: 'millisecond',
    tags,
  }),
  backoffDuration: defineMetric({
    name: 'brew.dependency.backoff.duration',
    type: 'distribution',
    unit: 'millisecond',
    tags,
  }),
}

export type ExternalRetryTelemetry = {
  /** Bounded dimensions declared by dependencyMetrics (domain, transport, method). */
  tags?: MetricTags
  /** Searchable correlation IDs; never promoted to metric dimensions. */
  context?: ObservabilityContext
  /** Owner-declared deadline; only the winning abort reason is a timeout. */
  timeoutSignal?: AbortSignal
}

type DependencyMetadata = {
  provider: string
  operation: string
  kind?: string
  maxAttempts: number
  outageBudgetMs?: number
  signal?: AbortSignal
  telemetry?: ExternalRetryTelemetry
  convex?: boolean
}

export type DependencyObservation = {
  attempt<T>(fn: () => Promise<T>): Promise<T>
  backoff(): void
  failedAttempt(error: unknown, attempt: number, retriesLeft: number): void
}

function safely(observe: () => void): void {
  try {
    observe()
  } catch {
    // Telemetry cannot change a provider result, error, retry, or cancellation.
  }
}

function statusCode(value: unknown): number | undefined {
  try {
    if (value instanceof Response) {
      return value.status
    }
    if (typeof value !== 'object' || value === null) {
      return undefined
    }
    const candidate = value as { status?: unknown; statusCode?: unknown }
    const status = candidate.status ?? candidate.statusCode
    return typeof status === 'number' &&
      Number.isInteger(status) &&
      status >= 100 &&
      status <= 599
      ? status
      : undefined
  } catch {
    // SDK failures can expose accessors; optional HTTP enrichment stays absent.
    return undefined
  }
}

function resultTags(
  metadata: DependencyMetadata,
  value: unknown,
  failed: boolean
): MetricTags {
  // Generic SDK results may have domain fields named status; only a Response
  // is an HTTP result. Thrown SDK failures can expose status/statusCode.
  const status =
    failed || value instanceof Response ? statusCode(value) : undefined
  let outcome = status !== undefined && status >= 400 ? 'failure' : 'success'
  if (failed) {
    if (metadata.signal?.aborted) {
      const timeoutSignal = metadata.telemetry?.timeoutSignal
      if (
        timeoutSignal?.aborted &&
        timeoutSignal.reason === metadata.signal.reason
      ) {
        // Preserve the abort object and SDK control flow while reporting the
        // owner's deadline consistently in dependency metrics and span errors.
        registerMetricErrorOutcome(timeoutSignal.reason, 'timeout')
        outcome = 'timeout'
      } else {
        outcome = 'cancelled'
      }
    } else {
      outcome = classifyMetricError(value)
    }
  }
  return {
    ...metadata.telemetry?.tags,
    provider: metadata.provider,
    operation: metadata.operation,
    kind: metadata.kind ?? 'unspecified',
    outcome,
    status_class:
      status === undefined ? 'none' : `${Math.floor(status / 100)}xx`,
  }
}

/**
 * One span and completion per logical call. Automatic HTTP spans remain the
 * physical transport attempts; this layer measures retry wait separately.
 * All SDK interactions are isolated from the callback, which runs exactly once
 * even if the tracer/context manager throws before or after invoking it.
 */
export async function observeDependency<T>(
  metadata: DependencyMetadata,
  fn: (observation: DependencyObservation) => Promise<T>
): Promise<T> {
  const startedAt = metricNow()
  let span: Span | undefined
  safely(() => {
    span = metadata.convex
      ? tracer.startSpan('convex.call', { startTime: startedAt })
      : tracer.startSpan('external.call', { startTime: startedAt })
  })
  safely(() => {
    span?.setAttributes(
      sanitizeSpanAttributes({
        ...contextSpanAttributes(metadata.telemetry?.context ?? {}),
        'brew.external.provider': metadata.provider,
        'brew.external.operation': metadata.operation,
        'brew.external.attempts.max': metadata.maxAttempts,
        'brew.external.domain': metadata.telemetry?.tags?.domain,
        'brew.external.transport': metadata.telemetry?.tags?.transport,
        'brew.external.method': metadata.telemetry?.tags?.method,
        ...(metadata.kind
          ? { 'brew.external.operation.kind': metadata.kind }
          : {}),
        ...(metadata.convex
          ? { 'brew.convex.function': metadata.operation }
          : {}),
        ...(metadata.outageBudgetMs === undefined
          ? {}
          : { 'brew.external.outage.budget_ms': metadata.outageBudgetMs }),
      })
    )
  })

  let attempts = 0
  let backoffStartedAt: number | undefined
  let backoffDurationMs = 0
  const finishBackoff = (finishedAt = metricNow()) => {
    if (backoffStartedAt === undefined) {
      return
    }
    backoffDurationMs += Math.max(0, finishedAt - backoffStartedAt)
    backoffStartedAt = undefined
  }
  const observation: DependencyObservation = {
    async attempt<TAttempt>(
      attempt: () => Promise<TAttempt>
    ): Promise<TAttempt> {
      finishBackoff()
      attempts += 1
      const attemptStartedAt = metricNow()
      let result: unknown
      let failed = false
      try {
        const value = await attempt()
        result = value
        return value
      } catch (error) {
        failed = true
        result = error
        throw error
      } finally {
        const attemptDurationMs = Math.max(0, metricNow() - attemptStartedAt)
        safely(() => {
          const options = {
            tags: resultTags(metadata, result, failed),
            context: metadata.telemetry?.context,
          }
          recordMetric(dependencyMetrics.attempts, 1, options)
          recordMetric(
            dependencyMetrics.attemptDuration,
            attemptDurationMs,
            options
          )
        })
      }
    },
    backoff() {
      backoffStartedAt ??= metricNow()
    },
    failedAttempt(error, attempt, retriesLeft) {
      safely(() => {
        const failedAt = metricNow()
        span?.addEvent(
          'external.retry.attempt_failed',
          {
            'brew.external.attempt': attempt,
            // OTel attributes must be finite; outage budgets have no count limit.
            ...(Number.isFinite(retriesLeft)
              ? { 'brew.external.retries_left': retriesLeft }
              : {}),
            'brew.external.error.type': normalizeError(error).kind,
            'brew.external.http.status_code': statusCode(error) ?? 0,
          },
          failedAt
        )
      })
    },
  }

  const run = async () => {
    let result: unknown
    let failed = false
    let completedAt = startedAt
    try {
      const value = await fn(observation)
      completedAt = metricNow()
      result = value
      return value
    } catch (error) {
      completedAt = metricNow()
      failed = true
      result = error
      safely(() => {
        if (resultTags(metadata, error, true).outcome === 'cancelled') {
          span?.addEvent(
            'external.retry.cancelled',
            {
              'brew.external.attempts.used': attempts,
            },
            completedAt
          )
          return
        }
        safely(() => {
          span?.addEvent(
            'external.retry.exhausted',
            {
              'brew.external.attempts.used': attempts,
              'brew.external.http.status_code': statusCode(error) ?? 0,
            },
            completedAt
          )
        })
        let errorKind = 'Error'
        safely(() => {
          errorKind = normalizeError(error).kind
        })
        // HTTP status already describes a returned failure; only thrown SDK
        // failures need an exception event and stack for investigation.
        if (errorKind !== 'RetryableResponseError') {
          recordSpanError(
            span,
            error,
            {
              'brew.external.error.type': errorKind,
            },
            completedAt
          )
        } else {
          span?.setStatus({ code: SpanStatusCode.ERROR })
        }
      })
      throw error
    } finally {
      finishBackoff(completedAt)
      const durationMs = Math.max(0, completedAt - startedAt)
      safely(() => {
        const metricTags = resultTags(metadata, result, failed)
        const status =
          failed || result instanceof Response ? statusCode(result) : undefined
        let legacyResult = 'failure'
        if (
          metricTags.outcome === 'success' ||
          metricTags.outcome === 'cancelled'
        ) {
          legacyResult = metricTags.outcome
        }
        span?.setAttributes({
          'brew.external.attempts.used': attempts,
          'brew.external.result': legacyResult,
          'brew.external.outcome': String(metricTags.outcome),
          'brew.external.duration_ms': durationMs,
          'brew.external.backoff.duration_ms': backoffDurationMs,
          ...(status === undefined
            ? {}
            : { 'brew.external.http.status_code': status }),
        })
        if (!failed && metricTags.outcome === 'failure') {
          span?.setStatus({ code: SpanStatusCode.ERROR })
        }
      })
      // A failing span implementation must not suppress otherwise healthy metrics.
      safely(() => {
        const options = {
          tags: resultTags(metadata, result, failed),
          context: metadata.telemetry?.context,
        }
        recordMetric(dependencyMetrics.completed, 1, options)
        recordMetric(dependencyMetrics.duration, durationMs, options)
        recordMetric(
          dependencyMetrics.backoffDuration,
          backoffDurationMs,
          options
        )
      })
      safely(() => span?.end(completedAt))
    }
  }

  let pending: Promise<T> | undefined
  const once = () => {
    pending ??= run()
    return pending
  }
  try {
    return await (span
      ? context.with(trace.setSpan(context.active(), span), once)
      : once())
  } catch {
    if (pending) {
      return pending
    }
    // No callback began: a context-manager failure must not skip the operation.
    return once()
  }
}
